import Paragraph from 'components/TextComps/Paragraph';
import Subtitle from 'components/TextComps/Subtitle';
import { useCallback, useMemo, useState } from 'react';

import { ReactComponent as FilterIcon } from 'images/icons/Filter.svg';
import { useTranslation } from 'react-i18next';
import { TrackmanEventFilter } from 'types/trackmanEvents';
import { EventsFiltersPanel } from '../EventsFiltersPanel/EventsFiltersPanel';
import { AppliedFiltersPanel } from '../AppliedFiltersPanel/AppliedFiltersPanel';
import { sortByDate } from 'utilities/dataSorters';
import { filtersConfig } from './availableFilters';
import { EventTileSkeleton } from '../EventTileSkeleton/EventTileSkeleton';
import { EventTile } from '../EventTile/EventTile';
import { useIsUserMember } from 'queryHooks/permissions';
import { useGetTrackmanEventsList } from 'queryHooks/trackmanEvents';
import { VideoModal } from '../VideoModal/VideoModal';

export const OnDemandRecordings = () => {
  const { t } = useTranslation();

  const trackmanEventsList = useGetTrackmanEventsList();
  const isUserMember = useIsUserMember();

  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<TrackmanEventFilter[]>([]);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);
  const [videoModalData, setVideoModalData] = useState({ url: '', title: '', allowedFeatures: '' });

  const pastEvents = useMemo(() => trackmanEventsList.data?.events.filter((event) => event.recordingReady) || [], [trackmanEventsList.data]);
  const pastEventsToDisplay = useMemo(() => {
    const filteredPastEvents = filters.length
      ? pastEvents.filter((event) => filters.some((filter) => event[filter.key] === filter.value))
      : pastEvents;
    return sortByDate(filteredPastEvents, (event) => event.startDate, 'DESCENDING');
  }, [filters, pastEvents]);

  const handleFilters = useCallback((filter: TrackmanEventFilter) => {
    const newFilters = !filters.find((f) => f.label === filter.label)
      ? [...filters, filter]
      : filters.filter((f) => f.label !== filter.label);
    setFilters(newFilters);
  }, [filters]);

  const clearFilters = useCallback(() => {
    setFilters([]);
  }, []);

  return (
    <div className={`events-subpage ${isFiltersPanelOpen ? 'filters-visible' : ''}`}>
      {isFiltersPanelOpen ? <div className='filters-background' onClick={() => setIsFiltersPanelOpen(false)} /> : null}
      <div className='page-heading'>
        <Subtitle>
          <>{t('OnDemandRecordings', { ns: 'trackmanEvents' })}</>
        </Subtitle>
        {pastEvents.length && !isFiltersPanelOpen ? (
          <div className='filters-button' onClick={() => setIsFiltersPanelOpen(!isFiltersPanelOpen)}>
            <FilterIcon /> <span>{t('Filter', { ns: 'trackmanEvents' })}</span>
          </div>
        ) : null}
      </div>
      {filters.length ? (
        <div className='applied-filters-bar'>
          <AppliedFiltersPanel filters={filters} onFilterClick={handleFilters} clearAllFilters={clearFilters} />
        </div>
      ) : null}
      <div className='events-list'>
        {pastEventsToDisplay.length ? (
          pastEventsToDisplay.map((event, i: number) => {
            return trackmanEventsList.isPending ? (
              <EventTileSkeleton key={`${event.title}+${i}-skeleton`} />
            ) : (
              <EventTile
                key={`${event.title}+${i}`}
                eventItem={event}
                isUserMember={isUserMember.data}
                setIsVideoModalOn={setIsVideoModalOpen}
                setVideoModalData={setVideoModalData}
              />
            );
          })
        ) : (
          <Paragraph>
            <>{t('ThereAreCurrentlyNoOnDemandEvents', { ns: 'trackmanEvents' })}</>
          </Paragraph>
        )}
      </div>
      <VideoModal isModalOn={isVideoModalOpen} setIsModalOn={setIsVideoModalOpen} modalData={videoModalData} />
      <EventsFiltersPanel
        open={isFiltersPanelOpen}
        filtersConfig={filtersConfig}
        selectedFilters={filters}
        onFilterClick={handleFilters}
        onClearFilters={clearFilters}
        showFilters={setIsFiltersPanelOpen}
      />
    </div>
  );
};
